import { useCallback, useEffect, useState } from "react";
import { ApprovedMailDomains } from "@/components/admin/ApprovedMailDomains";
import { CollapsedSection } from "@/components/admin/CollapsedSection";
import { QcMailRecipients } from "@/components/admin/QcMailRecipients";
import {
  readQcFailureEmailSettings,
  saveQcFailureEmailSettings,
  type QcFailureEmailSettings,
} from "@/lib/manufacturing/builderAssistantChecklistClient";
import {
  DEFAULT_QC_FAILURE_SUBJECT,
  QC_FAILURE_SUBJECT_TAGS,
  renderQcFailureSubject,
} from "@/lib/manufacturing/qcFailureEmail";

const BLANK: QcFailureEmailSettings = {
  enabled: false,
  from: "",
  subject: DEFAULT_QC_FAILURE_SUBJECT,
  include_link: false,
  no_reply: false,
};

export function MfgQcEmailSection() {
  const [draft, setDraft] = useState<QcFailureEmailSettings>(BLANK);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<{ text: string; ok: boolean } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const res = await readQcFailureEmailSettings();
    setLoading(false);
    if (!res.ok) {
      setMsg({ text: res.error, ok: false });
      return;
    }
    setDraft(res.data.settings.subject ? res.data.settings : { ...res.data.settings, subject: DEFAULT_QC_FAILURE_SUBJECT });
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function save() {
    setSaving(true);
    setMsg(null);
    const res = await saveQcFailureEmailSettings(draft);
    setSaving(false);
    if (!res.ok) {
      setMsg({ text: res.error, ok: false });
      return;
    }
    setDraft(res.data.settings);
    setMsg({ text: "QC email settings saved.", ok: true });
  }

  const preview = renderQcFailureSubject(draft.subject, {
    serial: "SC-1042",
    plant: "Cherokee",
    customer: "Ada Miller",
    model: "Lofted Barn",
    item: "Visible shiners",
    category: "WALLS",
    date: "2026-09-30",
  });

  return (
    <div className="space-y-4 px-1 py-2">
      <p className="text-xs text-gray-500 dark:text-zinc-400">
        Each event has its own list. QC, final QC, admins, and owners start on every list. The from address must be on a domain verified in Resend.
      </p>
      {msg ? (
        <div className={msg.ok
          ? "rounded-md bg-emerald-50 px-3 py-2 text-xs text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-100"
          : "rounded-md bg-rose-50 px-3 py-2 text-xs text-rose-800 dark:bg-rose-900/30 dark:text-rose-100"}
        >
          {msg.text}
        </div>
      ) : null}
      <CollapsedSection title="Approved email domains">
        <ApprovedMailDomains />
      </CollapsedSection>
      <QcMailRecipients
        failureSettings={(
          <>
      <label className="flex items-center gap-2 text-sm text-gray-800 dark:text-zinc-100">
        <input
          type="checkbox"
          checked={draft.enabled}
          disabled={loading || saving}
          onChange={(event) => setDraft((current) => ({ ...current, enabled: event.target.checked }))}
        />
        Email the org when an inspection is submitted with failures
      </label>
      {draft.enabled ? (
        <div className="space-y-3">
          <label className="block text-xs">
            <span className="font-semibold text-gray-700 dark:text-zinc-200">Send from</span>
            <input
              className="mt-1 w-full rounded-md border border-gray-300 bg-white px-2 py-1.5 text-sm dark:border-zinc-600 dark:bg-zinc-900"
              value={draft.from}
              disabled={saving}
              placeholder="qc@yourcompany.com"
              onChange={(event) => setDraft((current) => ({ ...current, from: event.target.value }))}
            />
            <span className="mt-1 block font-normal text-gray-500">Leave blank to use the platform sender.</span>
          </label>
          <label className="block text-xs">
            <span className="font-semibold text-gray-700 dark:text-zinc-200">Subject</span>
            <input
              className="mt-1 w-full rounded-md border border-gray-300 bg-white px-2 py-1.5 text-sm dark:border-zinc-600 dark:bg-zinc-900"
              value={draft.subject}
              disabled={saving}
              onChange={(event) => setDraft((current) => ({ ...current, subject: event.target.value }))}
            />
          </label>
          <div className="flex flex-wrap gap-1">
            {QC_FAILURE_SUBJECT_TAGS.map((tag) => (
              <button
                key={tag.tag}
                type="button"
                className="rounded-full bg-gray-100 px-2 py-0.5 text-[11px] text-gray-700 dark:bg-zinc-800 dark:text-zinc-200"
                onClick={() => setDraft((current) => ({ ...current, subject: `${current.subject} ${tag.tag}`.trim() }))}
              >
                {tag.tag} {tag.label}
              </button>
            ))}
          </div>
          <p className="text-xs text-gray-600 dark:text-zinc-300">Preview: {preview}</p>
          <label className="flex items-center gap-2 text-sm text-gray-800 dark:text-zinc-100">
            <input
              type="checkbox"
              checked={draft.include_link}
              disabled={saving}
              onChange={(event) => setDraft((current) => ({ ...current, include_link: event.target.checked }))}
            />
            Include a link to the read-only QC report
          </label>
          <label className="flex items-center gap-2 text-sm text-gray-800 dark:text-zinc-100">
            <input
              type="checkbox"
              checked={draft.no_reply}
              disabled={saving}
              onChange={(event) => setDraft((current) => ({ ...current, no_reply: event.target.checked }))}
            />
            Add “Do not reply to this email address” to the message
          </label>
        </div>
      ) : null}
      <button
        type="button"
        className="rounded-lg bg-indigo-700 px-3 py-1.5 text-sm font-semibold text-white disabled:opacity-60"
        disabled={loading || saving}
        onClick={() => void save()}
      >
        Save
      </button>
          </>
        )}
      />
    </div>
  );
}
